import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import {
  catchError,
  finalize,
  map,
  merge,
  shareReplay,
  take,
  takeUntil,
  tap,
  throwError,
  timer,
} from 'rxjs';
import { BaseDialog } from '../base-dialog';
import { PROMPT_DIALOG_IMPORTS } from '../dialog-imports';
import {
  PromptDialogData,
  PromptDialogInput,
  PromptDialogTextareaField,
  PromptDialogValidatorMap,
  PromptDialogValidators,
} from '../dialog.model';

const TEXTAREA_MIN_ROWS = 3;
const TEXTAREA_AUTORESIZE_MIN_ROWS = 3;
const TEXTAREA_AUTORESIZE_MAX_ROWS = 8;

@Component({
  selector: 'app-prompt-dialog',
  imports: [PROMPT_DIALOG_IMPORTS, ReactiveFormsModule, CdkTextareaAutosize],
  templateUrl: './prompt-dialog.component.html',
  styleUrl: './prompt-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PromptDialogComponent
  extends BaseDialog<PromptDialogData>
  implements OnInit
{
  private readonly _dialogRef =
    inject<MatDialogRef<PromptDialogComponent>>(MatDialogRef);

  protected readonly control = new FormControl();
  protected readonly submitted = signal(false);
  protected readonly loading = signal(false);

  constructor() {
    super();
  }

  protected get rows() {
    const input = this.data.input;

    if (this.isTextarea(input) && !this.isAutoResize(input)) {
      return input.rows ?? TEXTAREA_MIN_ROWS;
    }

    return null;
  }

  protected get minRows() {
    const input = this.data.input;

    if (this.isTextarea(input) && this.isAutoResize(input)) {
      return input.minRows ?? TEXTAREA_AUTORESIZE_MIN_ROWS;
    }

    return null;
  }

  protected get maxRows() {
    const input = this.data.input;

    if (this.isTextarea(input) && this.isAutoResize(input)) {
      return input.maxRows ?? TEXTAREA_AUTORESIZE_MAX_ROWS;
    }

    return null;
  }

  isTextarea(input: Partial<PromptDialogInput>): input is PromptDialogTextareaField {
    return input.mode === 'textarea';
  }

  isAutoResize(input: PromptDialogInput) {
    return this.isTextarea(input) && input.autoResize === true;
  }

  ngOnInit(): void {
    if (this.data.input?.validators) {
      const validators = this._getValidatorsFrom(this.data.input.validators);

      this.control.setValidators(validators);
    }

    if (this.data.input?.defaultValue) {
      this.control.setValue(this.data.input.defaultValue);
    }
  }

  get errorMessage() {
    if (!this.control.errors || !this.data.input?.validators) {
      return null;
    }

    const validatorErrorMessages = this._getValidatorMessages(
      this.data.input.validators
    );

    const errorMessages = Object.keys(this.control.errors).map((key) => {
      const validatorMessage = validatorErrorMessages[key];

      if (typeof validatorMessage === 'function') {
        return validatorMessage(this.control.getError(key));
      }

      return validatorMessage;
    });

    return errorMessages[0];
  }

  override handleConfirm(): void {
    if (
      this.data.onConfirm &&
      Object.prototype.toString.call(this.data.onConfirm) == '[object Function]'
    ) {
      this.submitted.set(true);
      this.control.disable();

      const delay = this.data.onConfirmLoadingDelay ?? 1000;

      const callback$ = this.data.onConfirm(this.control.value);
      const source$ = callback$.pipe(
        shareReplay(1),
        catchError((error) => throwError(() => error))
      );

      const loading$ = merge(
        timer(delay).pipe(
          map(() => true),
          takeUntil(source$)
        ),

        source$.pipe(map(() => false))
      );

      loading$
        .pipe(
          takeUntil(source$),
          tap(() => this.loading.set(true)),
          take(1)
        )
        .subscribe();

      source$
        .pipe(
          tap((result) => {
            this.loading.set(false);
            this._dialogRef.close(result);
          }),
          finalize(() => {
            this.submitted.set(false);
            this.loading.set(false);
            this.control.enable();
          }),
          take(1)
        )
        .subscribe();

      // loading$
      //   .pipe(
      //     tap((state) => this.loading.set(state)),
      //     filter((state) => !state),
      //     tap(() => this._dialogRef.close(true)),
      //     finalize(() => {
      //       this.submitted.set(false);
      //       this.loading.set(false);
      //       this.control.enable();
      //     }),
      //     take(1)
      //   )
      //   .subscribe();

      return;
    }

    this._dialogRef.close(true);
  }

  private _getValidatorsFrom(validators: PromptDialogValidators) {
    if (!validators) {
      return null;
    }

    return Object.values(validators).map((validatorMap) => validatorMap.validator);
  }

  private _getValidatorMessages(validators: PromptDialogValidators) {
    const validatorKeys = Object.keys(validators) as (keyof typeof validators)[];

    return validatorKeys.reduce(
      (previous, key) => {
        const { errorMessage } = validators[key];

        return (previous[key] = errorMessage), previous;
      },
      {} as Record<string, PromptDialogValidatorMap['errorMessage']>
    );
  }
}
